/**
 * 藝人總覽（artistAdminList）與待審內容（contentReviewQueue）types and helpers. Pure — no React / Firebase — so
 * node:test can import them. Shapes mirror functions/src/handlers/artist-admin.ts.
 */

export type ArtistProfileStatus = "published" | "unpublished" | "none";
export type ArtistVerifiedReason = "verified" | "not_published" | "no_owner" | "owner_suspended" | "owner_missing";

export interface ArtistAdminOwner {
  uid: string;
  email: string | null;
  displayName: string | null;
  suspended: boolean;
}
export interface ArtistAdminRow {
  /** actors doc id == artistProfiles doc id */
  artistId: string;
  /** actors.name */
  name: string;
  profileStatus: ArtistProfileStatus;
  /** artistProfiles.displayName */
  displayName: string | null;
  verified: boolean;
  verifiedReason: ArtistVerifiedReason;
  /** Active members with role "artist". */
  owners: ArtistAdminOwner[];
  followerCount: number;
  /** Released dramas (isReleased == true) listing this actor. */
  worksCount: number;
  /** Dramas listing this actor that are not released. */
  unreleasedWorksCount: number;
  featuredOrder: number | null;
}
export interface ArtistAdminListResult {
  items: ArtistAdminRow[];
}

export type ReviewContentType = "post" | "vlog" | "story";
export interface ReviewItem {
  artistId: string;
  artistName: string;
  contentId: string;
  type: ReviewContentType;
  title: string;
  /** ≤120 chars */
  bodyPreview: string;
  audience: string;
  version: number;
  /** epoch ms */
  submittedAt: number | null;
  submittedBy: string | null;
}
export interface ReviewQueuePage {
  items: ReviewItem[];
  nextCursor: string | null;
}

export const PROFILE_STATUS_LABELS: Record<ArtistProfileStatus, string> = {
  published: "已公開",
  unpublished: "已下架",
  none: "未建立",
};
export const VERIFIED_REASON_LABELS: Record<ArtistVerifiedReason, string> = {
  verified: "已認證",
  not_published: "專頁未公開",
  no_owner: "尚無藝人擁有者",
  owner_suspended: "擁有者已停權",
  owner_missing: "擁有者帳號不存在",
};
export const REVIEW_TYPE_LABELS: Record<ReviewContentType, string> = {
  post: "貼文",
  vlog: "Vlog",
  story: "限時動態",
};

/** Backend limits (functions/src/handlers/artist-admin.ts). */
export const ADMIN_FILTER_MAX = 200;
export const REVIEW_QUEUE_MAX = 50;

/** Response body → list result; tolerates a missing items array. */
export const toAdminList = (data: unknown): ArtistAdminListResult => ({
  items: Array.isArray((data as { items?: unknown })?.items) ? ((data as { items: ArtistAdminRow[] }).items) : [],
});
/** Response body → review page; tolerates missing fields. */
export const toReviewPage = (data: unknown): ReviewQueuePage => {
  const d = (data ?? {}) as { items?: unknown; nextCursor?: unknown };
  return {
    items: Array.isArray(d.items) ? (d.items as ReviewItem[]) : [],
    nextCursor: typeof d.nextCursor === "string" && d.nextCursor ? d.nextCursor : null,
  };
};
/** Clamps a requested page size to the backend range 1..50 (default 20). */
export const reviewQueueLimit = (limit?: number): number =>
  limit === undefined || !Number.isFinite(limit) ? 20 : Math.min(REVIEW_QUEUE_MAX, Math.max(1, Math.trunc(limit)));

export function artistAdminErrorMessage(code: string | undefined, status: number): string {
  if (status === 401 || code === "UNAUTHENTICATED") return "請重新登入後操作";
  if (status === 403 || code === "FORBIDDEN") return "需要管理員或工作人員權限";
  if (status === 404) return "後端尚未部署藝人總覽／待審內容功能（artistHub）";
  if (code === "INVALID_ARGUMENT") return "查詢條件不正確，請重新整理後再試";
  if (code === "FAILED_PRECONDITION" || status === 412) return "後端索引尚未建立完成，請稍後再試";
  return "操作未完成，請重試";
}
